import React, { FunctionComponent } from "react";
import { Plan, BenefitItemProps } from "../../../@types";
import { ItemIterator, CollapsibleBox } from "../../../components";
import {
  getBenefitCost,
  makeBenefitItemItems as mBII,
  useTranslate,
} from "../../../helpers";
import ComparisonDetailItem from "./ComparisonDetailItem";
import ComparisonBenefitItem from "./ComparisonBenefitItem";
import { shouldSuppressPlanBenefits } from "../../../helpers/planHelpers";

const BenefitsAndCosts: FunctionComponent<{ plan: Plan }> = ({ plan }) => {
  const makeBenefitItemItems = (
    items: Array<{
      label: string | JSX.Element;
      service?: string;
      suppressed?: boolean;
    }>,
    benefit: string
  ): BenefitItemProps[] => mBII(items, benefit, plan);

  const t = useTranslate();
  const suppressed = shouldSuppressPlanBenefits(plan);

  return (
    <div className="columnSection">
      {/* Doctor services */}
      <div className="planCompare__cell">
        <ItemIterator Component={ComparisonDetailItem}>
          {[
            {
              detail: getBenefitCost(t, {
                benefit: "doctor_visits",
                service: "primary",
                plan,
              }),
              label: t("plan_terms.primary_doctor_visit"),
              suppressed,
            },

            {
              detail: getBenefitCost(t, {
                benefit: "doctor_visits",
                service: "specialist",
                plan,
              }),
              label: t("plan_terms.specialist_visit"),
              suppressed,
            },
          ]}
        </ItemIterator>
      </div>

      {/* Tests, labs, imaging */}
      <div className="planCompare__cell mobileLabServices">
        <ItemIterator Component={ComparisonBenefitItem}>
          {[
            ...makeBenefitItemItems(
              [
                {
                  label: (
                    <CollapsibleBox
                      labelId="plan_terms.diagnostic_tests_and_procedures"
                      tealiumProps={{
                        event_action: "Plan Details",
                        event_label: "Toggle - diagnostic tests and procedures",
                      }}
                    >
                      {t(
                        "plan_terms.diagnostic_tests_and_procedures.definition"
                      )}
                    </CollapsibleBox>
                  ),
                  service: "diagnostic_tests",
                },
                {
                  label: t("plan_terms.lab_services"),
                  service: "lab_services",
                },
                {
                  label: t("plan_terms.diagnostic_radiology_services"),
                  service: "diagnostic_radiology_services",
                },
                {
                  label: t("plan_terms.outpatient_xrays"),
                  service: "outpatient_xrays",
                },
              ],
              "diagnostic_procedures"
            ),
            ...makeBenefitItemItems(
              [
                { label: t("plan_terms.emergency_care"), service: "emergency" },
                { label: t("plan_terms.urgent_care"), service: "urgent_care" },
              ],
              "emergency_care"
            ),
          ]}
        </ItemIterator>
      </div>

      {/* Hospital services */}
      <div className="planCompare__cell">
        <ItemIterator Component={ComparisonBenefitItem}>
          {[
            ...makeBenefitItemItems(
              [
                {
                  label: t("plan_terms.inpatient_hospital_coverage"),
                },
              ],
              "inpatient_hospital"
            ),
            ...makeBenefitItemItems(
              [
                {
                  label: t("plan_terms.outpatient_hospital_coverage"),
                },
              ],
              "outpatient_hospital"
            ),
          ]}
        </ItemIterator>
      </div>

      {/* Preventive services */}
      <div className="planCompare__cell">
        <ComparisonBenefitItem
          benefit="preventive_care"
          label={t("plan_terms.preventive_services")}
          mobileLabelOnly
          plan={plan}
          suppressed={suppressed}
        />
      </div>
    </div>
  );
};

export default BenefitsAndCosts;
